import React from 'react';
import { X } from 'lucide-react';
import { Document, Page } from 'react-pdf';
import type { Theme } from '../../themes/theme';
import type { Bookmark } from './types/pdf-viewer.types';

// Outline item interface
interface OutlineItem {
  title: string;
  dest: any;
  url?: string;
  items?: OutlineItem[];
}

// Recursive outline component
const OutlineItemRow: React.FC<{
  item: OutlineItem;
  onNavigate: (item: OutlineItem) => void;
  theme: Theme;
  level: number;
}> = ({ item, onNavigate, theme, level }) => {
  const [expanded, setExpanded] = React.useState(true);
  const hasChildren = item.items && item.items.length > 0;

  return (
    <div>
      <div
        className={`flex items-center gap-1 p-1 rounded cursor-pointer hover:${
          theme.colors.state.hover
        } ${level > 0 ? 'ml-4' : ''}`}
        onClick={() => {
          if (hasChildren) setExpanded(!expanded);
          onNavigate(item);
        }}
        style={{ paddingLeft: `${level * 12}px` }}
      >
        {hasChildren && (
          <span className={`text-xs ${theme.colors.text.secondary}`}>{expanded ? '▼' : '▶'}</span>
        )}
        <span className={`text-xs ${theme.colors.text.primary} flex-1 truncate`}>{item.title}</span>
      </div>
      {hasChildren && expanded && (
        <div className="ml-2">
          {item.items!.map((child, index) => (
            <OutlineItemRow
              key={index}
              item={child}
              onNavigate={onNavigate}
              theme={theme}
              level={level + 1}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// Thumbnails sidebar
interface ThumbnailsSidebarProps {
  theme: Theme;
  pdfUrl: string;
  numPages: number;
  currentPage: number;
  onPageSelect: (page: number) => void;
  onClose: () => void;
}

export const ThumbnailsSidebar: React.FC<ThumbnailsSidebarProps> = ({
  theme,
  pdfUrl,
  numPages,
  currentPage,
  onPageSelect,
  onClose,
}) => (
  <div
    className={`w-48 ${theme.colors.bg.secondary} border-r ${theme.colors.border.primary} overflow-y-auto flex-shrink-0`}
  >
    <div
      className={`p-2 border-b ${theme.colors.border.primary} sticky top-0 ${theme.colors.bg.secondary} z-10`}
    >
      <div className="flex items-center justify-between">
        <h3 className={`text-xs font-semibold ${theme.colors.text.primary}`}>Miniaturas</h3>
        <button
          onClick={onClose}
          className={`p-1 ${theme.colors.text.secondary} hover:${theme.colors.text.primary} rounded`}
        >
          <X size={14} />
        </button>
      </div>
    </div>
    <div className="p-2 space-y-2">
      {Array.from({ length: numPages }, (_, i) => i + 1).map((pageNum) => (
        <div
          key={pageNum}
          onClick={() => onPageSelect(pageNum)}
          className={`cursor-pointer border-2 rounded p-1 transition-all ${
            pageNum === currentPage
              ? `${theme.colors.border.focus} ${theme.colors.bg.tertiary}`
              : `${theme.colors.border.primary} hover:${theme.colors.border.focus}`
          }`}
        >
          <Document file={pdfUrl}>
            <Page
              pageNumber={pageNum}
              scale={0.15}
              renderTextLayer={false}
              renderAnnotationLayer={false}
            />
          </Document>
          <div className={`text-xs text-center mt-1 ${theme.colors.text.secondary}`}>{pageNum}</div>
        </div>
      ))}
    </div>
  </div>
);

// Outline sidebar
interface OutlineSidebarProps {
  theme: Theme;
  outline: OutlineItem[];
  onNavigate: (item: OutlineItem) => void;
  onClose: () => void;
}

export const OutlineSidebar: React.FC<OutlineSidebarProps> = ({
  theme,
  outline,
  onNavigate,
  onClose,
}) => (
  <div
    className={`w-64 ${theme.colors.bg.secondary} border-r ${theme.colors.border.primary} overflow-y-auto flex-shrink-0`}
  >
    <div
      className={`p-2 border-b ${theme.colors.border.primary} sticky top-0 ${theme.colors.bg.secondary} z-10`}
    >
      <div className="flex items-center justify-between">
        <h3 className={`text-xs font-semibold ${theme.colors.text.primary}`}>Índice</h3>
        <button
          onClick={onClose}
          className={`p-1 ${theme.colors.text.secondary} hover:${theme.colors.text.primary} rounded`}
        >
          <X size={14} />
        </button>
      </div>
    </div>
    <div className="p-2 space-y-1">
      {outline.map((item, index) => (
        <OutlineItemRow key={index} item={item} onNavigate={onNavigate} theme={theme} level={0} />
      ))}
    </div>
  </div>
);

// Bookmarks sidebar
interface BookmarksSidebarProps {
  theme: Theme;
  bookmarks: Bookmark[];
  currentPage: number;
  onGoToBookmark: (bookmark: Bookmark) => void;
  onRemoveBookmark: (id: string) => void;
  onClose: () => void;
}

export const BookmarksSidebar: React.FC<BookmarksSidebarProps> = ({
  theme,
  bookmarks,
  currentPage,
  onGoToBookmark,
  onRemoveBookmark,
  onClose,
}) => (
  <div
    className={`w-64 ${theme.colors.bg.secondary} border-r ${theme.colors.border.primary} overflow-y-auto flex-shrink-0`}
  >
    <div
      className={`p-2 border-b ${theme.colors.border.primary} sticky top-0 ${theme.colors.bg.secondary} z-10`}
    >
      <div className="flex items-center justify-between">
        <h3 className={`text-xs font-semibold ${theme.colors.text.primary}`}>Marcadores</h3>
        <button
          onClick={onClose}
          className={`p-1 ${theme.colors.text.secondary} hover:${theme.colors.text.primary} rounded`}
        >
          <X size={14} />
        </button>
      </div>
    </div>
    <div className="p-2 space-y-1">
      {bookmarks.map((bookmark) => (
        <div
          key={bookmark.id}
          className={`flex items-center justify-between p-2 rounded hover:${
            theme.colors.state.hover
          } cursor-pointer ${bookmark.page === currentPage ? theme.colors.bg.tertiary : ''}`}
          onClick={() => onGoToBookmark(bookmark)}
        >
          <div className="flex-1 min-w-0">
            <div className={`text-xs ${theme.colors.text.primary} truncate`}>{bookmark.label}</div>
            <div className={`text-[10px] ${theme.colors.text.secondary}`}>
              Página {bookmark.page}
            </div>
          </div>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onRemoveBookmark(bookmark.id);
            }}
            className={`p-1 ${theme.colors.text.secondary} hover:${theme.colors.action.danger} rounded`}
            title="Eliminar marcador"
          >
            <X size={12} />
          </button>
        </div>
      ))}
    </div>
  </div>
);
